import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  input,
  OnDestroy,
  output,
  inject,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { isNil, valueOrDefault } from '@web/utils';
import { Environment, ENVIRONMENT } from '@web/config';
import { AlertsService } from '../../../alert/services/alerts.service';
import { AlertsComponent } from '../../../alert/components/alerts/alerts.component';
import { PayuPrivacyPolicyComponent } from '../payu-privacy-policy/payu-privacy-policy.component';
import { AssetImage } from '../../../image/enums/asset-image';
import { ImageThumbnailComponent } from '../../../image/components/image-thumbnail/image-thumbnail.component';
import { AssetImageUrlPipe } from '../../../image/pipes/asset-image-url.pipe';

type SecureFormField = 'number' | 'date' | 'cvv';

interface SecureFormOptions {
  cardIcon: boolean;
  style: {
    basic: {
      fontSize: string;
    };
  };
  placeholder: {
    number: string;
    date: string;
    cvv: string;
  };
  lang: string;
}

interface SecureFormFieldInstance {
  render: (selector: string) => void;
}

interface TokenizeError {
  message: string;
  code: string;
  type: string;
}

interface TokenizeResult {
  status: 'SUCCESS' | 'ERROR';
  body?: {
    token: string;
  };
  error?: {
    messages: TokenizeError[];
  };
}

interface PayUSdkInstance {
  secureForms: () => {
    add: (
      type: SecureFormField,
      options: SecureFormOptions,
    ) => SecureFormFieldInstance;
  };
  tokenize: (
    type: 'SINGLE' | 'MULTI' | 'SINGLE_LONG_TERM',
  ) => Promise<TokenizeResult>;
}

declare global {
  interface Window {
    PayU: (posId: string) => PayUSdkInstance;
  }
}

@Component({
  selector: 'app-payu-card-number-widget',
  imports: [
    AlertsComponent,
    PayuPrivacyPolicyComponent,
    ImageThumbnailComponent,
    AssetImageUrlPipe,
    NgOptimizedImage,
  ],
  templateUrl: './payu-card-number-widget.component.html',
  styleUrl: './payu-card-number-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PayuCardNumberWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly environment = inject<Environment>(ENVIRONMENT);
  private readonly alerts = inject(AlertsService);

  public readonly withPrivacyPolicy = input<boolean>(true);
  public readonly tokenizeSuccessEvent = output<string>();
  public readonly tokenizeErrorEvent = output<void>();

  protected readonly AssetImage = AssetImage;

  private payuSdkForms!: PayUSdkInstance;
  private secureForms!: ReturnType<PayUSdkInstance['secureForms']>;
  /* prettier-ignore */
  private readonly sdkScriptId = '9b445a77-8e3e-4b90-8f8d-136d2de54d09';
  private readonly optionsForms: SecureFormOptions = {
    cardIcon: true,
    style: {
      basic: {
        fontSize: '24px',
      },
    },
    placeholder: {
      number: '',
      date: 'MM/YY',
      cvv: '',
    },
    lang: 'pl',
  };
  /* prettier-ignore */
  private readonly messages: Record<string, () => string> = {
    'error.validation.card.empty': () => 'Pusty numer karty.',
    'error.validation.card.length': () => 'Nieprawidłowa długość numeru karty.',
    'error.validation.card.number': () => 'Nieprawidłowy numer karty.',
    'error.validation.card.unsupported': () => 'Niewspierany rodzaj karty. Obsługiwane typy karty to Visa, Mastercard i Maestro.',
    'error.validation.expDate.empty': () => 'Pusta data ważności karty.',
    'error.validation.expDate.past': () => 'Data ważności karty jest z przeszłości.',
    'error.validation.expDate.value': () => 'Data ważności karty jest nieprawidłowa.',
    'error.validation.cvv.empty': () => 'Pusty CVV.',
    'error.validation.cvv.value': () => 'Nieprawidłowy CVV.',
    'error.tokenization': () => 'Wystąpił nieznany błąd podczas tokenizacji.',
    'error.send.cvv': () => 'Wystąpił nieznany błąd podczas tokenizacji.',
    'error.network': () => 'Wystąpił nieznany błąd podczas tokenizacji.',
  };

  public ngAfterViewInit(): void {
    this.loadPayUSdk()
      .then(() => this.initializePayuForms())
      .catch((err) => console.error('Błąd ładowania SDK PayU:', err));
  }

  public ngOnDestroy(): void {
    const script = document.getElementById(this.sdkScriptId);

    if (script) {
      script.remove();
    }
  }

  public tokenize(): void {
    this.alerts.clear();

    if (!this.payuSdkForms) {
      return;
    }

    this.payuSdkForms
      .tokenize('MULTI')
      .then((result: TokenizeResult) => {
        if (result.status === 'SUCCESS') {
          const { body } = result;

          if (isNil(body)) {
            console.error('Wystąpił nieznany błąd.');

            return;
          }

          this.tokenizeSuccessEvent.emit(body.token);
        } else {
          this.alerts.showMultiple((builder) =>
            valueOrDefault(result.error?.messages, []).map((error) => {
              const message = this.messages[error.code];

              return builder.buildFailure(message());
            }),
          );

          this.tokenizeErrorEvent.emit();
        }
      })
      .catch((e) => console.error('Błąd tokenizacji:', e));
  }

  private loadPayUSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById(this.sdkScriptId);

      if (existingScript) {
        return resolve();
      }

      const script = document.createElement('script');

      script.id = this.sdkScriptId;
      script.src = this.sdkScriptUrl();
      script.async = true;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Nie udało się załadować SDK PayU.'));
      };

      document.body.appendChild(script);
    });
  }

  private initializePayuForms(): void {
    if (isNil(window.PayU)) {
      console.error('PayU SDK nie jest dostępne.');

      return;
    }

    const { PosId } = this.environment.PayU;

    this.payuSdkForms = window.PayU(PosId);
    this.secureForms = this.payuSdkForms.secureForms();

    const cardNumber = this.secureForms.add('number', this.optionsForms);
    const cardDate = this.secureForms.add('date', this.optionsForms);
    const cardCvv = this.secureForms.add('cvv', this.optionsForms);

    cardNumber.render('#payu-card-number');
    cardDate.render('#payu-card-date');
    cardCvv.render('#payu-card-cvv');
  }

  private sdkScriptUrl(): string {
    const { Sandbox } = this.environment.PayU;

    if (Sandbox) {
      return 'https://secure.snd.payu.com/javascript/sdk';
    }

    return 'https://secure.payu.com/javascript/sdk';
  }
}
